import {
  Heart,
  Award,
  CheckCircle,
  Crown,
  Sparkles,
  Stethoscope,
} from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { SEO } from '@/components/SEO'
import '@/styles/animations.css'

const privilegeCategories = [
  {
    category: 'Annual Wellness & Screening',
    categoryIcon: Stethoscope,
    headerTag: null as string | null,
    benefits: [
      { title: 'Comprehensive Oral Health Check-up', desc: 'Advanced preventive assessment for Member & Spouse', tag: 'Included with Membership' as string | null },
      { title: 'Full Mouth Digital Screening with Reports', desc: 'Advanced diagnostic evaluation with detailed dental report', tag: 'Included with Membership' as string | null },
      { title: 'Annual Oral Cancer Screening', desc: 'Early detection screening for improved long-term oral health', tag: 'Included with Membership' as string | null },
    ],
  },
  {
    category: 'Treatment Privileges',
    categoryIcon: Award,
    headerTag: null as string | null,
    benefits: [
      { title: 'Member Preferred Pricing on Scaling & Tooth-Coloured Fillings', desc: 'Exclusive preventive & restorative care access', tag: null as string | null },
      { title: 'Preferred Member Rates on Root Canal & Crown Treatments', desc: 'Advanced treatments at special member pricing', tag: null as string | null },
      { title: 'Smile Design & Cosmetic Consultation', desc: 'Personalised aesthetic dental planning', tag: 'Exclusive member privileges' as string | null },
    ],
  },
  {
    category: 'Family & Kids Protection',
    categoryIcon: Heart,
    headerTag: 'Included with Membership' as string | null,
    benefits: [
      { title: 'Fluoride Protection for Children (Below 12)', desc: 'Preventive enamel strengthening treatment', tag: 'Included with Membership' as string | null },
      { title: 'Orthodontic Early Assessment for Kids', desc: 'Growth & alignment evaluation for timely correction', tag: 'Included with Membership' as string | null },
      { title: 'Extended Family Coverage', desc: 'Spouse and dependents enjoy member privileges', tag: 'Included with Membership' as string | null },
    ],
  },
  {
    category: 'VIP Lifestyle Perks',
    categoryIcon: Crown,
    headerTag: 'Exclusive member privileges' as string | null,
    benefits: [
      { title: 'Priority Appointment Scheduling', desc: 'Members receive priority consultation slots', tag: null as string | null },
      { title: 'Birthday Month Dental Care Privilege', desc: 'Special dental hygiene benefit during your birthday month', tag: null as string | null },
      { title: 'Priority Emergency Consultation', desc: 'One priority emergency dental consultation per year', tag: null as string | null },
    ],
  },
]

const costBreakdown = [
  { item: 'Annual Oral Health Check-up', cost: '₹1,500' },
  { item: 'Full Mouth Digital Screening', cost: '₹2,500' },
  { item: 'Oral Cancer Screening', cost: '₹1,500' },
  { item: 'Kids Fluoride', cost: '₹1,000' },
  { item: 'Family Consult', cost: '₹1,500' },
  { item: 'Emergency', cost: '₹1,500' },
]

const perfectFor = [
  'Families who want regular dental check-ups',
  'Parents who want preventive care for children',
  'Patients planning cosmetic or restorative treatments',
  'Individuals who value priority dental care access',
]

export function WelcomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-950">
      <SEO
        title="Privilege Care Membership | Sri Sai Sabari Dental Clinic"
        description="Protect your smile with our Privilege Care Membership — comprehensive preventive dentistry program for families. Annual screenings, oral cancer screening, kids orthodontic assessment, and exclusive treatment privileges worth ₹18,000+ annually."
        keywords="dental membership Chennai, preventive dentistry, dental care membership, Sri Sai Sabari Dental Clinic, family dental plan, oral cancer screening, kids dental care Madipakkam"
        canonical="/welcome"
      />
      <Header currentPage="welcome" ctaText="Activate Your Privilege Membership" ctaLink="/book-appointment" />

      {/* ================================================================== */}
      {/* HERO                                                                */}
      {/* ================================================================== */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/10 via-transparent to-transparent"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
            Protect Your Smile{' '}
            <em className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent font-bold">Before Problems</em>{' '}
            Start
          </h1>
          <h2 className="text-xl md:text-2xl font-bold text-gray-200 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
            Preventive Dentistry Program for Families
          </h2>
          <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
            At <span className="text-white font-semibold">Sri Sai Sabari Dental Clinic</span>, we believe prevention is always better than treatment.
            Our Privilege Care Membership is designed to help families maintain excellent oral health
            through regular screenings, preventive care, and <em className="text-yellow-400/90 not-italic font-medium">exclusive member privileges</em>.
          </p>
        </div>
      </section>

      {/* ================================================================== */}
      {/* STRUCTURED BENEFITS (2x2 Grid)                                     */}
      {/* ================================================================== */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {privilegeCategories.map((cat, ci) => (
              <div
                key={ci}
                className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-yellow-500/20 transition-all duration-500 animate-fade-in-up"
                style={{ animationDelay: `${0.2 + ci * 0.1}s`, animationFillMode: 'both' }}
              >
                {/* Category Header */}
                <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-xl flex items-center justify-center flex-shrink-0">
                      <cat.categoryIcon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white">{cat.category}</h3>
                  </div>
                  {cat.headerTag && (
                    <span className="text-yellow-400/60 text-[11px] italic whitespace-nowrap">{cat.headerTag}</span>
                  )}
                </div>

                {/* Benefits List */}
                <div className="space-y-5">
                  {cat.benefits.map((benefit, bi) => (
                    <div key={bi} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <h4 className="text-white font-semibold text-sm leading-snug">{benefit.title}</h4>
                          {benefit.tag && (
                            <span className="text-yellow-400/60 text-[11px] italic whitespace-nowrap flex-shrink-0 mt-0.5">{benefit.tag}</span>
                          )}
                        </div>
                        <p className="text-gray-500 text-xs mt-1">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Embedded Value Comparison card — only in VIP Lifestyle Perks */}
                {ci === 3 && (
                  <div className="mt-6 bg-white/[0.04] border border-yellow-500/20 rounded-xl p-4 text-center">
                    <p className="text-gray-400 text-xs font-semibold tracking-wide uppercase mb-1">Membership Value Comparison</p>
                    <p className="text-3xl md:text-4xl font-black text-white">
                      ₹8,300<span className="text-yellow-500">+</span>
                    </p>
                    <p className="text-gray-500 text-[11px] mt-2 leading-snug">
                      Estimated annual dental care savings, for you and your entire family —
                      included with your family membership
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* BOTTOM: Value + Breakdown + Perfect For                            */}
      {/* ================================================================== */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in-up" style={{ animationDelay: '0.7s', animationFillMode: 'both' }}>

            {/* Total Annual Benefits Value */}
            <div className="bg-gradient-to-br from-yellow-600/15 to-yellow-700/10 border border-yellow-500/20 rounded-2xl p-6 md:p-8 text-center">
              <p className="text-yellow-400/70 text-[10px] font-bold tracking-[0.2em] uppercase mb-3">Total Annual Benefits Value</p>
              <p className="text-5xl md:text-6xl font-black text-white mb-3">
                ₹18,000<span className="text-yellow-500">+</span>
              </p>
              <p className="text-gray-400 text-xs leading-relaxed">
                Estimated annual dental care benefits, with ₹16,000+ for you and your family — Included with Care Membership
              </p>
            </div>

            {/* Membership Value Comparison */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 md:p-8">
              <h4 className="text-white font-bold text-base mb-4">Membership Value Comparison</h4>
              <div className="space-y-2.5">
                {costBreakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">• {item.item}</span>
                    <span className="text-white font-semibold text-xs">{item.cost}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
                <span className="text-yellow-400 text-xs font-bold tracking-wide uppercase">Annual Care Value</span>
                <span className="text-yellow-400 text-sm font-black">₹8,500+</span>
              </div>
              <p className="text-gray-500 text-[10px] mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-yellow-500/50" /> Exclusive member privileges
              </p>
            </div>

            {/* This Membership is Perfect For */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 md:p-8">
              <h4 className="text-white font-bold text-base mb-4">This Membership is Perfect For:</h4>
              <div className="space-y-3">
                {perfectFor.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
